import { env } from '@/utils/env';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { QueryClientProvider } from '@tanstack/react-query';

import * as duckdb from '@duckdb/duckdb-wasm';
import coi_pthread_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-coi.pthread.worker.js?url';
import coi_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-coi.worker.js?url';
import eh_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url';
import mvp_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url';
import duckdb_wasm_coi from '@duckdb/duckdb-wasm/dist/duckdb-coi.wasm?url';
import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url';
import duckdb_wasm from '@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url';
import * as Sentry from '@sentry/react';

import '@analytical-alley/ui/index.css';
import './index.css';

import { App } from '@/App';
import { AuthProvider } from '@/features/Auth';
import { queryClient } from '@/queryClient';
import { DuckDBProvider } from '@analytical-alley/duckdb-react';

Sentry.init({
    dsn: env.VITE_SENTRY_URL,
    enabled: process.env.NODE_ENV !== 'development',
    _experiments: { enableLogs: true },
    integrations: [
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: true,
            networkDetailAllowUrls: ['https://app.analyticalalley.com/v1/*'],
        }),
        Sentry.browserTracingIntegration(), // send console.log, console.error, and console.warn calls as logs to Sentry
        Sentry.consoleLoggingIntegration({ levels: ['log', 'error', 'warn'] }),
    ],
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['app.analyticalalley.com'],
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found!');

const root = createRoot(container);

const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.DEBUG);

const DUCKDB_BUNDLES: duckdb.DuckDBBundles = {
    mvp: {
        mainModule: duckdb_wasm,
        mainWorker: mvp_worker,
    },
    eh: {
        mainModule: duckdb_wasm_eh,
        mainWorker: eh_worker,
    },
    coi: {
        mainModule: duckdb_wasm_coi,
        mainWorker: coi_worker,
        pthreadWorker: coi_pthread_worker,
    },
};

root.render(
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <DuckDBProvider logger={logger} bundles={DUCKDB_BUNDLES}>
                <AuthProvider>
                    <App />
                </AuthProvider>
            </DuckDBProvider>
        </QueryClientProvider>
    </StrictMode>,
);
